/**
 * F-3 景點（admin.spot 權限）+ 景點多媒體插槽。
 *
 * 插槽的 `media_type` 沿用媒體庫的 {@link MediaType}，故本檔依賴 `./media`
 * （單向；媒體庫不反向依賴景點）。
 */

import { apiFetch, type Schemas } from './core';
import type { MediaType } from './media';

/**
 * 智慧窗屏內容欄位（純文字多語系）。
 *
 * 與 `description_i18n`（RichText HTML）分離：窗屏讀的是 `1_str*.txt` /
 * `2_str*.txt` / `roiN.txt` 這類純文字檔，HTML 會原樣顯示標籤。
 */
export interface SpotDisplayContent {
  location_name_i18n?: Record<string, string> | null;
  poi_title_i18n?: Record<string, string> | null;
  poi_desc_i18n?: Record<string, string> | null;
  intro_i18n?: Record<string, string> | null;
  detail_title_i18n?: Record<string, string> | null;
  detail_text_i18n?: Record<string, string> | null;
}

/**
 * 注意 `latitude` / `longitude` 是 **numeric string** 不是 number。
 * 先前手寫成 `number | string` 是不確定下的保險寫法；spec 已定死為字串。
 */
export type Spot = Schemas['SpotResponseDto'];

export function listSpots(includeDeleted = false): Promise<Spot[]> {
  const qs = includeDeleted ? '?include_deleted=true' : '';
  return apiFetch<Spot[]>(`/spots${qs}`, { method: 'GET' });
}

export function getSpot(id: string): Promise<Spot> {
  return apiFetch<Spot>(`/spots/${encodeURIComponent(id)}`, { method: 'GET' });
}

export interface CreateSpotDto extends SpotDisplayContent {
  spot_code: string;
  name_i18n: Record<string, string>;
  description_i18n?: Record<string, string>;
  latitude: number;
  longitude: number;
  trigger_radius_m?: number;
  roi_id?: number;
}

export interface UpdateSpotDto extends SpotDisplayContent {
  name_i18n?: Record<string, string>;
  description_i18n?: Record<string, string>;
  latitude?: number;
  longitude?: number;
  trigger_radius_m?: number;
  /** 傳 null 清空（景點退出窗屏導覽）；不傳表示不變更 */
  roi_id?: number | null;
  /**
   * 機翻待校記號的增減（逐格合併，不是整包取代）：
   * `{ poi_title_i18n: { en: 'mt_draft' } }`；值傳 null 代表「人已校對，拿掉記號」。
   *
   * 由 CMS 負責標記——伺服器無從得知這次送上來的英文是機器翻的還是人打的。
   */
  translation_state?: Record<string, Record<string, 'mt_draft' | null>>;
}

export function createSpot(dto: CreateSpotDto): Promise<Spot> {
  return apiFetch<Spot>('/spots', { method: 'POST', body: dto });
}

export function updateSpot(id: string, dto: UpdateSpotDto): Promise<Spot> {
  return apiFetch<Spot>(`/spots/${encodeURIComponent(id)}`, { method: 'PATCH', body: dto });
}

export function publishSpot(id: string): Promise<Spot> {
  return apiFetch<Spot>(`/spots/${encodeURIComponent(id)}/publish`, { method: 'POST' });
}

export function unpublishSpot(id: string): Promise<Spot> {
  return apiFetch<Spot>(`/spots/${encodeURIComponent(id)}/unpublish`, { method: 'POST' });
}

/** 軟刪除景點；BE 回 204 No Content。 */
export function deleteSpot(id: string): Promise<void> {
  return apiFetch<void>(`/spots/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

// ---------- 景點多媒體插槽（admin.spot 權限）----------

/** 對應窗屏檔案：1_pic*.png / 1_voice1.wav / 2_video1.mp4 / 2_voice1.wav */
export type SpotMediaSlot = 'poi_image' | 'intro_voice' | 'detail_video' | 'detail_voice';

/** 窗屏語系代碼（檔案樹用），與 CMS 的 zh-TW/en/ja 不同。 */
export type DisplayLocaleCode = 'zh' | 'en' | 'jp';

export interface SpotMediaEntry {
  id: string;
  slot: SpotMediaSlot;
  locale: DisplayLocaleCode;
  seq: number;
  media_id: string;
  filename: string;
  media_type: MediaType;
  mime_type: string;
  size_bytes: string;
}

/**
 * 保留手寫：generated 版的 `slots` 是兩層 `Record<string, …>`（OpenAPI 的
 * additionalProperties 表達不了「鍵必為 slot / locale 列舉」），改用它會讓
 * `slots[slot][locale]` 失去鍵的型別檢查。欄位集合仍與 spec 一致。
 */
export interface SpotMediaList {
  spot_id: string;
  slots: Record<SpotMediaSlot, Record<DisplayLocaleCode, SpotMediaEntry[]>>;
  poi_image_counts: Record<DisplayLocaleCode, number>;
  /** false = 各語系圖片張數不一致，產生器會拒絕產出發布包 */
  poi_image_consistent: boolean;
}

export function listSpotMedia(spotId: string): Promise<SpotMediaList> {
  return apiFetch<SpotMediaList>(`/spots/${encodeURIComponent(spotId)}/media`, { method: 'GET' });
}

/** 整批設定某插槽某語系；傳入順序即 1_pic1…1_picN 的順序。 */
export function setSpotMediaSlot(
  spotId: string,
  slot: SpotMediaSlot,
  locale: DisplayLocaleCode,
  mediaIds: string[],
): Promise<SpotMediaList> {
  return apiFetch<SpotMediaList>(`/spots/${encodeURIComponent(spotId)}/media/${slot}/${locale}`, {
    method: 'PUT',
    body: { media_ids: mediaIds },
  });
}

export function clearSpotMediaSlot(
  spotId: string,
  slot: SpotMediaSlot,
  locale: DisplayLocaleCode,
): Promise<SpotMediaList> {
  return apiFetch<SpotMediaList>(`/spots/${encodeURIComponent(spotId)}/media/${slot}/${locale}`, {
    method: 'DELETE',
  });
}

// ---------- 從媒體庫資料夾套用 ----------

export type SpotFolderPlan = Schemas['SpotFolderPlanResponseDto'];
export type SpotFolderPlanIssue = Schemas['SpotFolderPlanIssueDto'];
/** 推薦點層的未套用檔案；`reason` 列舉與景點層不同（多了 misplaced / conflict）。 */
export type SpotPoiPlanIssue = Schemas['SpotPoiPlanIssueDto'];
export type PlannedPoi = Schemas['PlannedPoiDto'];

/**
 * 依窗屏檔名規格把 `{folder}/{lang}/` 底下的檔案歸位到插槽與文字欄位。
 *
 * 對應規則**只存在於後端**（`spot-folder-import.rules.ts`）。這裡刻意不重算
 * 一份：規則同時放兩邊遲早會漂移成兩套，而漂移的症狀是「船上播錯內容」，
 * 不是任何一邊會報錯。CMS 只負責把後端算好的計畫畫出來。
 */
export function applySpotFolder(
  spotId: string,
  folder: string,
  opts: { dryRun: boolean; overwrite: boolean },
): Promise<SpotFolderPlan> {
  return apiFetch<SpotFolderPlan>(`/spots/${encodeURIComponent(spotId)}/apply-folder`, {
    method: 'POST',
    body: { folder, dry_run: opts.dryRun, overwrite: opts.overwrite },
  });
}

// ---------- 機器翻譯建議 ----------

export type SpotTranslateResult = Schemas['SpotTranslateResultDto'];
export type SpotTranslateItem = SpotTranslateResult['items'][number];
/** 建議屬於哪一筆推薦點；景點自己的欄位不帶。 */
export type SpotTranslatePoiRef = NonNullable<SpotTranslateItem['poi']>;

/** 可機翻的目標語系（CMS i18n 鍵）；與後端 TRANSLATE_TARGET_LOCALES 一致。 */
export type TranslateTargetLocale = 'en' | 'ja';

/**
 * 以繁中主稿產生英日翻譯建議。
 *
 * **不寫入任何東西**——回來的是對照表，要不要採用由人決定：景點欄位走
 * {@link updateSpot}（隨表單儲存），推薦點欄位走 {@link upsertSpotPoi}（即時儲存）。
 * 未設定供應商時後端回 503，訊息會講去哪裡設定。
 */
export function translateSpot(
  spotId: string,
  opts: {
    targets?: readonly TranslateTargetLocale[];
    fields?: readonly string[];
    overwrite?: boolean;
  } = {},
): Promise<SpotTranslateResult> {
  return apiFetch<SpotTranslateResult>(`/spots/${encodeURIComponent(spotId)}/translate`, {
    method: 'POST',
    body: {
      ...(opts.targets ? { targets: [...opts.targets] } : {}),
      ...(opts.fields ? { fields: [...opts.fields] } : {}),
      overwrite: opts.overwrite === true,
    },
  });
}

export type AssignFileResult = Schemas['AssignFileResponseDto'];

/** 手動指派的目標；與後端的 union 對應——媒體要插槽、文字要欄位。 */
export type AssignFileTarget =
  | { kind: 'media'; slot: SpotMediaSlot; locale: DisplayLocaleCode }
  | { kind: 'text'; field: string; locale: DisplayLocaleCode };

/**
 * 把一個檔名不符規格的素材手動指派到某一格。
 *
 * 補的是 `applySpotFolder` 的死角：操作者自己蒐集的素材（`IMG_2043.png`）永遠
 * 對應不到任何欄位，原本只能在「未套用的檔案」清單裡看著它。
 *
 * **指派是覆蓋**：單檔插槽直接取代，`poi_image` 接在既有清單之後。
 */
export function assignSpotFile(
  spotId: string,
  mediaId: string,
  target: AssignFileTarget,
): Promise<AssignFileResult> {
  return apiFetch<AssignFileResult>(`/spots/${encodeURIComponent(spotId)}/assign-file`, {
    method: 'POST',
    body: { media_id: mediaId, ...target },
  });
}

// ---------- 窗屏產出預覽 ----------

export interface SpotPreviewFile {
  path: string;
  /** 空字串 = 不分語系（roiN.txt）。 */
  locale: string;
  source: 'generated' | 'media' | 'qr';
  /** QR 在發布時才算得出大小，故為 null。 */
  size: number | null;
  field: string;
}

export interface SpotPreviewGap {
  severity: 'blocker' | 'warning' | 'info';
  message: string;
}

/**
 * §15 `download_url` 的六個桶子。預覽階段值是完整位址
 * （`http://…:9000/boat-release/rel_{version}/assets/12/zh/1_pic1.png`），發布後
 * `{version}` 換成實際版本號並附上簽章參數；主機、bucket、資產路徑三段不變。
 *
 * 岸上沒設 `BOAT_S3_ENDPOINT` 時退回相對路徑，並在 `gaps` 出一則 warning。
 */
export interface SpotPreviewDownloadUrls {
  video: string[];
  pic: string[];
  wav: string[];
  txt: string[];
  /** ASR 動態詞表產生器未實作，恆為空陣列。 */
  keyword: string[];
  /** `config/` 三個檔，全船共用。 */
  GPS: string[];
}

/**
 * 若現在發布，船機會對這個景點做什麼。
 *
 * - `add`：船上還沒有這個景點，會整包下載
 * - `modify`：船上已有，這次發布會覆蓋掉
 * - `delete`：船上已有但景點已取消發布，船機會刪掉 `assets/N`
 * - `none`：不會出現在 `attractions[]` 裡（尚未發布，船上也沒有）
 */
export type SpotPreviewOp = 'add' | 'modify' | 'delete' | 'none';

/**
 * 這個景點在 §15 回應的 `attractions[]` 裡的一筆——**一個語系一筆**。
 *
 * 不含外層信封（`status` / `server_time` / `data.info`）——那一層是整包回應共用
 * 的，船機一次請求拿到的是一個信封配 N 筆。
 *
 * 兩種形狀：要下載的給 `download_url`，要刪掉的給 `delete_paths`。真實回應就是
 * 這樣分的，預覽跟著分才不會讓人以為 delete 也要下載那幾百個檔。
 */
export type SpotPreviewAttraction = SpotPreviewUpsertAttraction | SpotPreviewDeleteAttraction;

export interface SpotPreviewUpsertAttraction {
  op: Exclude<SpotPreviewOp, 'delete'>;
  attraction_id: number | null;
  attraction_name: string;
  roi: string | null;
  /** `assets/12/en`——實際路徑，不是佔位符。 */
  target_path: string | null;
  download_url: SpotPreviewDownloadUrls;
}

export interface SpotPreviewDeleteAttraction {
  op: 'delete';
  attraction_id: number;
  attraction_name: string;
  /** 船機要刪掉的資料夾，例如 `assets/12`。 */
  delete_paths: string[];
}

/**
 * 保留手寫：generated 版把 `counts` / `cms_update` 標成
 * `Record<string, unknown>`（OpenAPI 的 additionalProperties 表達不了巢狀的
 * 固定形狀），用它會讓每個欄位存取都得先斷言。
 */
export interface SpotDisplayPreview {
  spot_id: string;
  spot_code: string;
  roi_id: number | null;
  is_published: boolean;
  files: SpotPreviewFile[];
  counts: {
    total: number;
    by_locale: Record<'zh' | 'en' | 'jp', number>;
    by_group: { roi: number; level1: number; level2: number; poi: number };
  };
  gaps: SpotPreviewGap[];
  /** 通常 zh / en / jp 三筆；整個景點要從船上移除時收斂成一筆。 */
  cms_update: SpotPreviewAttraction[];
}

/** 不寫入任何東西；可在每次切到預覽分頁時重算。 */
export function getSpotDisplayPreview(spotId: string): Promise<SpotDisplayPreview> {
  return apiFetch<SpotDisplayPreview>(`/spots/${encodeURIComponent(spotId)}/display-preview`, {
    method: 'GET',
  });
}
